import type { Command, Connection, Data, WsMeshServer } from './types.ts';
import type { WebsocketConnection, WebsocketConnectionFactory } from '../../common/ws/types.ts';

export type ServerCallbacks = {
    onConnect: (source: string, target: string, connection: Connection) => void
    onDisconnect: (source: string, target: string) => void;
    onReceive: (target: string, source: string, msg: Data['data']) => void
    onRegister?: (node: string, owner: string, endpoint: string) => void;
    onUnregister?: (node: string) => void;
}

type NodeConnection = {
    readonly from: string;
    readonly to: string,
    readonly connection: Connection
}

function ensureNodeConnection(from: string, to: string, connected: Map<string, NodeConnection>, relayConnection: WebsocketConnection<Command>) {
    const k = `${from}:${to}`;
    const c = connected.get(k);
    if (c) {
        return c.connection;
    }
    const connection = new class implements Connection {
        close(): void {
            relayConnection.send({type: 'bye', from: to, to: from});
            connected.delete(k);
        }

        send(cmd: Command): void {
            relayConnection.send(cmd);
        }

        toJSON() {
            return {type: 'relay', from, to};
        }
    };
    connected.set(k, {from, to , connection});
    return connection;
}

function processMessage(connected: Map<string, NodeConnection>, callbacks: ServerCallbacks, relayConnection: WebsocketConnection<Command>, msg: Command) {
    switch (msg.type) {
        case 'hello': {
            const { from, to } = msg;
            const connection = ensureNodeConnection(from, to, connected, relayConnection);
            callbacks.onConnect(to, from, connection);
            break;
        }
        case 'bye': {
            const { from, to } = msg;
            callbacks.onDisconnect(to, from);
            break;
        }
        case 'data': {
            const { from, to, data } = msg;
            callbacks.onReceive(to, from, data);
            break;
        }
    }
}
type RelayConnection = {
    node: string,
    owner: string,
    connected: Map<string, NodeConnection>,
    // the relay connection to the cluster
    connection: WebsocketConnection<Command>
}
export function createRelay(factory: WebsocketConnectionFactory<Command>,
                            endpoint: string,
                            callbacks: ServerCallbacks): WsMeshServer {
    // node id -> RelayConnection
    const relays = new Map<string, RelayConnection>();

    return {
        register: (node: string, replicas: string[]) => {
            const existing = relays.get(node);
            if (existing !== undefined) {
                const owner = replicas[0];
                if (existing.owner === owner) {
                    return;
                }
                else {
                    // re-connect to the new owner
                    existing.connection.disconnect();
                    existing.owner = owner;
                    existing.connection.connect();
                    callbacks.onRegister?.(node, owner, existing.connection.url);
                    return;
                }
            }
            else {
                const connected = new Map<string, NodeConnection>();
                const url = `${endpoint}?node=${node}`;
                const connection = factory.create(url, [], {
                    dynamicArguments: (url: string | URL) => {
                        const owner = relays.get(node)?.owner;
                        if (typeof url === 'string') {
                            url = `${url}&owner=${owner ?? ''}`;
                        }
                        else {
                            url.searchParams.append('owner', owner ?? '');
                        }
                        return { url };
                    }
                });
                const owner = replicas[0];
                connection.onConnect = (url) => {
                    callbacks.onRegister?.(node, owner, connection.url);
                    connection.send({ type: 'hello', from: node, to: 'all' });
                };
                connection.onReceive = (msg) => {
                    processMessage(connected, callbacks, connection, msg);
                };
                connection.onDisconnect = () => {
                    for (const [, c] of connected) {
                        c.connection.close();
                        callbacks.onDisconnect(c.to, c.from);
                    }
                };
                relays.set(node, { node, owner, connected, connection });

                connection.connect();
            }
        },
        unregister: (node: string) => {
            const relayConnection = relays.get(node);
            if (relayConnection !== undefined) {
                relayConnection.connection.close();
                callbacks.onUnregister?.(node);
            }
            relays.delete(node);
        },
        stop: () => {
            for (const [, relayConnection] of relays) {
                relayConnection.connection.close();
            }
            relays.clear();
        }
    };
}
